/**
 * Primary vertical nav tree for **BHYBE navigation shell only**.
 * Classic Materio layout uses {@link VerticalNavItems} from `src/gyg-navigation/vertical` unchanged.
 * Must be a `use*` hook (uses GrowthBook + auth hooks).
 */

import { useGrowthBook } from '@/src/context/GrowthBookProvider'
import { FeatureFlag } from '@/src/types/common'
import { VerticalNavItemsType } from 'src/@core/layouts/types'
import { useAuth } from '@/src/hooks/useAuth'
import { ROUTES } from '@/src/utils/routes'
import { getAskGygUrl } from '@/src/modules/ask-gyg/url'
import { GYG_NAV_LABELS } from '@/src/gyg-navigation/navLabels'

export const useBhyvePrimaryNavItems = (growthbook?: any, auth?: any): VerticalNavItemsType => {
  // Always call hooks first, then use provided values if available
  const hookGrowthbook = useGrowthBook()
  const hookAuth = useAuth()

  const growthbookData = growthbook || hookGrowthbook
  const authData = auth || hookAuth

  const isMasterProductListEnabled = growthbookData?.isOn(FeatureFlag.MASTER_PRODUCT_LIST) || false
  const isFranchiseModuleEnabled = growthbookData?.isOn(FeatureFlag.FRANCHISE_HUB) || false
  const isOrderManagementEnabled = growthbookData?.isOn(FeatureFlag.ORDER_MANAGEMENT) || false
  const isRecipeGuideEnabled = growthbookData?.getFeatureValue(FeatureFlag.RECIPE_MANAGEMENT, false) || false
  const isRecipeGuideCafeHolaEnabled = growthbookData?.isOn(FeatureFlag.RECIPE_GUIDE_CAFE_HOLA_TYPE) || false
  const isKitchenManagementEnabled = growthbookData?.isOn(FeatureFlag.KITCHEN_MANAGEMENT) || false

  // TODO: Re-enable with GYG Oracle menu.
  // const isRestaurantInsightsEnabled = growthbookData?.isOn(FeatureFlag.RESTAURANT_INSIGHTS) || false
  const isDigitalTrainingPlannerEnabled = growthbookData?.isOn(FeatureFlag.DIGITAL_TRAINING_PLANNER) || false
  const isAskGygEnabled = (growthbookData?.isOn(FeatureFlag.ASK_GYG) || false) && !!getAskGygUrl()

  return [
    {
      title: 'Home',
      path: ROUTES.RESTAURANT,
      icon: 'tabler:home',
      subject: 'restaurant',
      action: 'read'
    },
    ...(isAskGygEnabled
      ? [
          {
            title: 'Ask GYG',
            icon: 'tabler:robot',
            path: '/app/ask-gyg',
            customPermissionCheck: (user: any) => {
              if (user?.role?.id === 1) return true
              const handle = user?.role?.handle

              return handle === 'COC' || handle === 'SCOC' || handle === 'STA'
            }
          }
        ]
      : []),
    {
      title: 'Restaurants',
      icon: 'mynaui:store',
      subject: 'restaurant',
      action: 'read',
      children: [
        {
          title: 'Allocation & Delegation',
          path: '/app/restaurant/allocation',
          icon: 'mdi:account-multiple',
          subject: 'restaurant-allocation',
          action: 'read',

          // Custom check: requires both restaurant-allocation and restaurant-delegation read permissions
          customPermissionCheck: (user: any) => {
            const hasAllocationRead = user?.permissions?.['restaurant-allocation']?.includes('read') || false
            const hasDelegationRead = user?.permissions?.['restaurant-delegation']?.includes('read') || false

            return hasAllocationRead && hasDelegationRead
          }
        },
        {
          title: GYG_NAV_LABELS.disabledIngredientsDashboard,
          path: '/app/disabled-ingredients',
          icon: 'bxs:disabled-ingredients',
          subject: 'restaurant',
          action: 'read'
        },
        {
          title: 'Surcharges',
          path: '/app/marketing-portal/store-surcharges',
          icon: 'bxs:offer',
          subject: 'marketing',
          action: 'read'
        },
        {
          title: 'Menu Jobs',
          path: '/app/incomplete-menu-jobs',
          icon: 'bxs:menu',
          subject: 'job-management',
          action: 'read'
        },
        {
          title: GYG_NAV_LABELS.rawData,
          icon: 'mdi:database-export-outline',
          subject: 'restaurant',
          action: 'read',
          children: [
            {
              title: 'Ready To Roll',
              path: '/app/submissions',
              icon: 'mynaui:store',
              subject: 'restaurant',
              action: 'read'
            },
            {
              title: 'Weekly Audit Logs',
              path: '/app/audit-logs/reports',
              icon: 'mdi:file-document-multiple-outline',
              subject: 'restaurant',
              action: 'read'
            },
            {
              title: GYG_NAV_LABELS.disabledIngredientsReports,
              path: '/app/disabled-ingredients/reports',
              icon: 'mdi:file-chart-outline',
              subject: 'restaurant',
              action: 'read'
            }
          ]
        }
      ]
    },
    {
      title: GYG_NAV_LABELS.guestExperience,
      icon: 'ant-design:customer-service-outlined',
      path: '/app/customer-portal',
      subject: 'guest-experience',
      action: 'read'
    },
    ...(isOrderManagementEnabled
      ? [
          {
            title: 'Order Management',
            icon: 'mynaui:cart',
            path: '/app/order-management',
            subject: 'order-management',
            action: 'read'
          }
        ]
      : []),
    {
      title: 'Marketing',
      icon: 'pixelarticons:chart',
      subject: 'marketing',
      action: 'read',
      children: [
        {
          title: 'Offers',
          path: '/app/marketing-portal/offers',
          icon: 'bxs:offer',
          subject: 'marketing',
          action: 'read'
        },
        {
          title: 'Nutritional Info',
          path: '/app/marketing-portal/nutritional-info',
          subject: 'ingredient-nutritional-info',
          action: 'read'
        },
        {
          title: 'Nutritional Used Quantity',
          path: '/app/marketing-portal/nutritional-info/used-quantity',
          subject: 'ingredient-nutritional-info',
          action: 'read'
        }
      ]
    },
    ...(isKitchenManagementEnabled
      ? [
          {
            title: 'Kitchen Management',
            icon: 'mdi:chef-hat',
            subject: 'kitchen-management',
            action: 'read',
            children: [
              {
                title: 'OMS Restaurants',
                path: '/app/kitchen-management/oms-restaurants',
                icon: 'mdi:store',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Apps',
                path: '/app/kitchen-management/oms-apps',
                icon: 'mdi:application-outline',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Products',
                path: '/app/kitchen-management/oms-products',
                icon: 'mdi:food',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Bundles',
                path: '/app/kitchen-management/oms-bundles',
                icon: 'mdi:food',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Settings',
                path: '/app/kitchen-management/oms-settings',
                icon: 'mdi:cog',
                subject: 'kitchen-management',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    ...(isDigitalTrainingPlannerEnabled
      ? [
          {
            title: 'Training Management',
            icon: 'mdi:clipboard-text-outline',
            subject: 'digital-training-planner',
            action: 'read',
            children: [
              {
                title: 'Plan Settings',
                path: '/app/training-planner/catalog',
                icon: 'mdi:bookmark-multiple-outline',
                subject: 'digital-training-planner',
                action: 'read'
              },
              {
                title: 'Template Plans',
                path: '/app/training-planner/templates',
                icon: 'mdi:clipboard-text-outline',
                subject: 'digital-training-planner',
                action: 'read'
              },
              {
                title: 'Completion Records',
                path: '/app/reporting/training-planner',
                icon: 'mdi:chart-box-outline',
                subject: 'digital-training-planner',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    ...(isFranchiseModuleEnabled
      ? [
          {
            title: 'Franchise Hub',
            icon: 'mdi:account-group',
            subject: 'franchise-hub',
            action: 'read',
            children: [
              {
                title: 'Franchise List',
                path: '/app/franchise-hub',
                icon: 'mdi:account-group',
                subject: 'franchise-hub',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    ...(isRecipeGuideEnabled
      ? [
          {
            title: 'Recipes',
            icon: 'mdi:book-open-outline',
            subject: 'recipe',
            action: 'read',
            children: [
              {
                title: 'All Recipes',
                path: '/app/recipe-mgmt',
                icon: 'mdi:account-group',
                subject: 'recipe',
                action: 'read'
              },
              {
                title: 'Equipments',
                path: '/app/recipe-guide-equipments',
                icon: 'mdi:account-group',
                subject: 'recipe-guide-equipment',
                action: 'read'
              },
              {
                title: 'Hazard Tags',
                path: '/app/food-safety-tags',
                icon: 'mdi:tag',
                subject: 'recipe',
                action: 'read'
              },
              {
                title: 'Portioning Tools',
                path: '/app/portioning-tools',
                icon: 'mdi:ruler',
                subject: 'portioning-tool',
                action: 'read'
              },
              {
                title: 'Ingredients',
                path: '/app/recipe-guide-ingredients',
                icon: 'mdi:account-group',
                subject: 'recipe',
                action: 'read'
              },
              ...(isRecipeGuideCafeHolaEnabled
                ? [
                    {
                      title: 'Recipe Making Tips',
                      path: '/app/recipe-making-tips',
                      icon: 'mdi:lightbulb-on-outline',
                      subject: 'recipe',
                      action: 'read'
                    },
                    {
                      title: 'QRG Cafe Hola',
                      path: '/app/recipe-qrg',
                      icon: 'mdi:file-pdf-box',
                      subject: 'recipe',
                      action: 'update'
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
    {
      title: 'User Management',
      icon: 'tabler:users',
      path: '/app/user-management',
      subject: 'user-management',
      action: 'read',
      children: [
        {
          title: 'User Role',
          path: '/app/user-management',
          icon: 'bxs:offer',
          subject: 'user-management',
          action: 'manage'
        },

        // {
        //   title: 'Role Delegation',
        //   path: '/app/role-delegation',
        //   icon: 'mdi:account-switch',
        //   subject: 'user-management',
        //   action: 'manage'
        // },
        {
          title: 'User List',
          path: '/app/user-management/list',
          icon: 'mingcute:inventory-line',
          subject: 'user-management',
          action: 'read'
        },
        {
          title: 'User Profile',
          path: authData.user
            ? `/app/user-management/profile/?userId=${authData.user.id}`
            : '/app/user-management/profile',
          icon: 'mdi:account-circle',
          subject: 'user-management',
          action: 'read'
        },
        {
          title: 'History Logs',
          path: '/app/user-management/changes',
          icon: 'mingcute:inventory-line',
          subject: 'user-management',
          action: 'read'
        }
      ]
    },
    ...(isMasterProductListEnabled
      ? [
          {
            title: 'Products',
            path: '/app/products',
            icon: 'material-symbols:contract-outline',
            subject: 'product',
            action: 'read'
          }
        ]
      : []),

    // TODO: Re-enable GYG Oracle (Restaurant Insights / AI Insights) when ready.
    // ...(isRestaurantInsightsEnabled
    //   ? [
    //       {
    //         title: 'GYG Oracle',
    //         icon: 'tabler:device-analytics',
    //         subject: 'restaurant',
    //         action: 'read',
    //         children: [
    //           {
    //             title: 'Restaurant Insights',
    //             path: '/app/restaurant-insights',
    //             icon: 'tabler:chart-line',
    //             subject: 'restaurant',
    //             action: 'read'
    //           },
    //           {
    //             title: 'AI Insights',
    //             path: '/app/ai-insights',
    //             icon: 'tabler:robot',
    //             subject: 'restaurant',
    //             action: 'read'
    //           }
    //         ]
    //       }
    //     ]
    //   : []),
    {
      title: GYG_NAV_LABELS.howToGuide,
      icon: 'ic:outline-quiz',
      path: '/app/faq',
      subject: 'faq',
      action: 'read'
    },
    {
      title: 'Admin Tools',
      icon: 'tabler:tools',
      bhyveRailVariant: 'admin-tools',
      children: [
        {
          title: 'Announcements',
          path: '/app/announcements',
          icon: 'tabler:megaphone',
          subject: 'admin-tools.announcements',
          action: 'manage'
        },
        {
          title: 'Menu Equipments',
          path: '/app/menu-equipments',
          icon: 'mdi:chef-hat',
          subject: 'admin-tools.menu-equipment',
          action: 'manage'
        },
        {
          title: 'Ingredients',
          path: '/app/admin-tools-ingredients',
          icon: 'mdi:food-variant',
          subject: 'admin-tools.ingredients',
          action: 'manage'
        },
        {
          title: 'Cron Jobs',
          path: '/app/cron-jobs',
          icon: 'tabler:clock-cog',
          subject: 'admin-tools.cronjobs',
          action: 'manage'
        },
        {
          title: 'Feature experiences',
          path: ROUTES.ADMIN_FEATURE_EXPERIENCES,
          icon: 'mdi:flag-variant-outline',
          subject: 'admin-tools.feature-experiences',
          action: 'read'
        }
      ]
    }
  ]
}
